import { billingApi } from "./billingApi";
import { BillingReason, SubscriptionStatus } from "./billingCodes";
import { formatMoney } from "./amountDue";
import { formatPrice, getPlanPrice } from "../../pages/auth/register/planPricing";

// Enterprise: one packet whose prices and resources are each organization's
// own, agreed with our team (backend c70492f2). getPackets never returns it, so
// the app knows it by its id.
//
// Picked with nothing agreed yet, it is only asked for, with the calls any plan
// is picked with: POST subscriptions (no subscription running: the
// organization waits at billing_only, reason ENTERPRISE_OFFER_PENDING) or
// change-plan (a plan running: it carries on). Our team then sets the terms in
// Stripe, and the billing status reports them as `enterpriseOffer`. They are
// taken up through the same two calls, monthly or annual, under the same rules
// as any plan (planChanges.js). The terms in force are `enterprisePlan`; they,
// never the packet, say what the plan costs and what it allows.

/** The Enterprise packet. Fixed, and the same in every environment. */
export const ENTERPRISE_PACKET_ID = "1c2ec301-b688-4c0f-8961-1c3e96579e7b";

/**
 * One organization's Enterprise prices and resources, as the billing status
 * reports them: `enterpriseOffer` (offered, not taken up yet) and
 * `enterprisePlan` (in force), beside `enterpriseRequested` (asked for, not
 * taken up yet).
 *
 * @typedef {Object} EnterpriseTerms
 * @property {number} monthlyAmount  cents a month, on the monthly plan
 * @property {number} yearlyAmount  cents a month, on the annual plan (12 months)
 * @property {string} currency  "usd", "eur"… lower case
 * @property {number|null} workspaceLimit  active workspaces; null is no limit
 * @property {number|null} projectLimit  active projects in each workspace; null is no limit
 * @property {string} termsId  GUID of this set of terms. An offer keeps it when it is taken up and
 *    becomes `enterprisePlan`; a change of the terms in force, limits alone included, gives a new one
 * @property {string} offeredAt  ISO, UTC; when the terms were offered, for display only
 */

const ENTERPRISE_NAME = "Enterprise";

/**
 * Enterprise as a plan picker picks it: by its id. What it costs, when it costs
 * anything yet, is the organization's terms.
 */
export const ENTERPRISE_PACKET = Object.freeze({ id: ENTERPRISE_PACKET_ID, name: ENTERPRISE_NAME });

/** The annual plan's terms, under its per-month price. */
export const ENTERPRISE_ANNUAL_NOTE =
   "Billed monthly, 12-month commitment. Cancelling it ends it when the 12 months are up.";

export const isEnterprisePacket = (packetId) =>
   typeof packetId === "string" && packetId.toLowerCase() === ENTERPRISE_PACKET_ID;

/** The terms the organization is on now, while its plan is Enterprise. */
export const currentEnterpriseTerms = (status) =>
   isEnterprisePacket(status?.packetId) ? status?.enterprisePlan ?? null : null;

/**
 * The terms picking Enterprise takes up: the offer when there is one, or else
 * the terms the organization had — subscribing again after a cancellation —
 * as the backend picks them (EnterprisePlans.ForTargetAsync). Null when none
 * are agreed, and picking it only asks for them.
 */
export const enterpriseTermsToTakeUp = (status) =>
   status?.enterpriseOffer ?? status?.enterprisePlan ?? null;

/**
 * The terms an Enterprise subscription is charged at: the ones it starts on
 * while it waits for its first card, and the ones in force once it runs
 * (EnterprisePlans.ForSubscriptionAsync).
 */
export const enterpriseTermsBilled = (status) =>
   status?.subscriptionStatus === SubscriptionStatus.NONE
      ? enterpriseTermsToTakeUp(status)
      : status?.enterprisePlan ?? null;

/**
 * Whether terms our team offered wait for an organization with no
 * subscription: what it is held at billing_only for, and what the billing page
 * starts out on.
 */
export const isOfferWaiting = (status) =>
   status?.reason === BillingReason.ENTERPRISE_OFFER_PENDING && Boolean(status?.enterpriseOffer);

/** Whether Enterprise was asked for and our team hasn't offered its terms yet. */
export const isEnterpriseRequestOpen = (status) =>
   Boolean(status?.enterpriseRequested) && !status?.enterpriseOffer;

/** What the terms charge a month on the option shown, in their currency: "$500.00". */
export const formatEnterprisePrice = (terms, isYearly) =>
   terms ? formatMoney(isYearly ? terms.yearlyAmount : terms.monthlyAmount, terms.currency) : null;

const describeLimit = (limit, singular) =>
   typeof limit === "number"
      ? `${limit.toLocaleString()} ${limit === 1 ? singular : `${singular}s`}`
      : `Unlimited ${singular}s`;

/** What the terms allow, a line each: "3 workspaces", "Unlimited projects per workspace". */
export const describeEnterpriseLimits = (terms) => [
   describeLimit(terms?.workspaceLimit, "workspace"),
   `${describeLimit(terms?.projectLimit, "project")} per workspace`
];

/**
 * Terms as a plan the plan-change rules weigh (planChanges.js): per-month
 * prices in whole units, ranked like any packet's, and the limits the way a
 * packet carries them, 0 for none — as the backend turns the packet into the
 * organization's plan (EnterprisePlans.Apply). Two sets of terms are two plans
 * on the one packet, told apart by `termsId`. `terms` is kept, so the prices
 * are printed in their own currency.
 */
export const enterprisePlanOf = (terms) =>
   terms
      ? {
           id: ENTERPRISE_PACKET_ID,
           name: ENTERPRISE_NAME,
           monthlyPrice: terms.monthlyAmount / 100,
           annuallyPrice: terms.yearlyAmount / 100,
           workspaceLimit: terms.workspaceLimit ?? 0,
           projectDashboardLimit: terms.projectLimit ?? 0,
           termsId: terms.termsId ?? null,
           terms
        }
      : null;

/** A plan's per-month price as its card prints it: a packet's ("$99"), or Enterprise terms' ("€450.00"). */
export const formatPlanPrice = (plan, isYearly) =>
   plan?.terms ? formatEnterprisePrice(plan.terms, isYearly) : formatPrice(getPlanPrice(plan, isYearly));

const currencyOf = (plan) => plan?.terms?.currency?.toLowerCase() ?? "usd";

/**
 * An amount a change between two plans comes to, in whole units, in their
 * currency. Packets are priced in dollars. Null when the two plans are priced
 * in different currencies, where a difference means nothing.
 */
export const formatAmountBetween = (amount, from, to) => {
   if (typeof amount !== "number" || currencyOf(from) !== currencyOf(to)) return null;
   if (!from?.terms && !to?.terms) return formatPrice(amount);
   return formatMoney(Math.round(amount * 100), currencyOf(to));
};

/**
 * Whether the organization has Enterprise terms to take up now, read fresh. The
 * calls that ask for Enterprise take up its terms instead once there are any,
 * at once on a card already on file, so terms offered since the page last read
 * are shown before anything is sent, never bought unseen. The read keeps the
 * status (billingApi.js), so the page shows them. Rejects when it can't read.
 */
export const hasEnterpriseTermsNow = async (organizationId) =>
   Boolean(enterpriseTermsToTakeUp(await billingApi.getBillingStatus(organizationId)));
